import { useContext, useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';

import { listUsers } from 'services/calendarServices';
import { getActivityLogs } from 'services/activitylogServices';

import { getTimeStampEmbeddedDate } from 'utils/utility_functions/formatTimeAndDate';
import { addDays, subtractDays } from 'utils/timeDateUtils';
import { generateFullName } from 'utils/generateFullNameUtils';

import ActivityLogTable from './ActivityLogTable';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { sortByKey } from 'utils/utils';
import { getDateBoundaryInTimezone } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { activityLogModuleFilter, caseLeadsFilter } from 'constants/dropdownOptions';
import { getCase } from 'services/CaseServices';
import { getLeads } from 'services/leadServices';
import Cookies from 'universal-cookie';
import { totalCountEvent } from 'services/generalServices';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';

// import 'assets/scss/style.scss';

const ActivityLog = (props) => {
  const [activityLog, setActivityLog] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedUserList, setSelectedUserList] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [dateFilter, setDateFilter] = useState('');
  const [filterBy, setFilterBy] = useState();
  const [module, setModule] = useState();
  const [moduleSection, setModuleSection] = useState();
  const [searchKey, setSearchKey] = useState('');
  const [caseListOptions, setCaseListOptions] = useState([]);
  const [leadListOptions, setLeadListOptions] = useState([]);
  const [customDateRange, setCustomDateRange] = useState(null);
  const cookie = new Cookies();

  const userContext = useContext(UserDetailsContext);

  const [currentDateToDisplay, setCurrentDateToDisplay] = useState({
    start: new Date(),
    end: new Date(),
  });

  const isCaseSpecific = props?.isCaseSpecific ?? false;
  const isFromTeamManagement = props?.isFromTeamManagement ?? false;
  const isFromDashBoard = props?.isFromDashBoard ?? false;
  const isFromReports = props?.isFromReports ?? false;
  const userId = props?.userDetails?.client_cognito_username;

  let todayDate = new Date();

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line

  const loadUsers = (case_id = props?.caseDetails?.case_id) => {
    listUsers(case_id)
      .then((response) => setUsersList(sortByKey(response.data, 'first_name')))
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getDataOfActivityLog('initial', customDateRange);
  }, [selectedUserList, filterBy, moduleSection, dateFilter, customDateRange]);

  const fetchActivityLogData = (backward = false, forward = false) => {
    if (backward) {
      getDataOfActivityLog('backward');
    } else if (forward) {
      let currentDate = new Date();

      if (endDate.toDateString() !== currentDate.toDateString()) {
        getDataOfActivityLog('forward');
      }
    }
  };

  function getDataOfActivityLog(type, date) {
    let tempStartDate = new Date();
    let tempEndDate = new Date();

    let tempFormattedStartDate = new Date();
    let tempFormattedEndDate = new Date();

    if (type === 'forward') {
      tempStartDate = addDays(endDate, 1);
      tempEndDate = addDays(endDate, 2);
    } else if (type === 'backward') {
      tempStartDate = subtractDays(endDate, 3);
      tempEndDate = subtractDays(endDate, 2);
    } else if (type === 'initial') {
      tempStartDate = subtractDays(endDate, 1);
    }

    tempFormattedStartDate = getTimeStampEmbeddedDate(tempStartDate, false, true);
    tempFormattedEndDate = getTimeStampEmbeddedDate(tempEndDate, false, true);

    setEndDate(tempEndDate);
    setCurrentDateToDisplay({ start: tempStartDate, end: tempEndDate });
    setActivityLog([]);

    let data = {
      key: '',
      status: '',
      type: filterBy ? filterBy : '',
      sub_type: '',
      user_id: isFromTeamManagement ? userId : selectedUserList?.client_cognito_username ? selectedUserList?.client_cognito_username : '',
      case_id: isCaseSpecific ? props?.caseDetails?.case_id : moduleSection ? moduleSection : '',
      // start_timestamp: getDateBoundaryInTimezone(tempFormattedStartDate, userContext?.userDetails?.timezone, 'start'),
      // end_timestamp: getDateBoundaryInTimezone(tempFormattedEndDate, userContext?.userDetails?.timezone, 'end'),
      start_timestamp: date?.start_date
        ? date?.start_date
        : getDateBoundaryInTimezone(date?.start_date, userContext?.userDetails?.timezone, 'start'),
      end_timestamp: date?.end_date
        ? date?.end_date
        : getDateBoundaryInTimezone(date?.start_date, userContext?.userDetails?.timezone, 'end'),
      pagination_limit: 1000,
    };

    getActivityLogData(data);
  }

  function getActivityLogData(data) {
    getActivityLogs(data)
      .then((response) => {
        setActivityLog([...response.data]);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };

  const pickExtractedDate = (dateArray) => {
    // TimezoneDatePicker already returns UTC ISO strings with proper start/end of day times
    // dateArray[0] = start of day in user timezone (converted to UTC)
    // dateArray[1] = end of day in user timezone (converted to UTC)
    return {
      start_date: dateArray?.[0] || '',
      end_date: dateArray?.[1] || dateArray?.[0] || '',
    };
  };

  const onChangeCalendar = (value) => {
    setDateFilter(value);
    if (value) {
      setCustomDateRange(pickExtractedDate(value));
    } else {
      setCustomDateRange(null);
    }
  };

  const refreshCase = () => {
    setSearchKey('');
    setDateFilter('');
    setCustomDateRange(null);
    setSelectedUserList(null);
    setModule();
    setModuleSection();
    setFilterBy();

    let filters = {
      payment_date_upper: '',
      payment_date_lower: '',
      start_date: '',
      end_date: '',
      search_key: '',
      practice_area: '',
    };
    if (props.caseView) {
      filters.case_id = props?.caseDetails?.case_id;
    }
  };

  const onChangeCaseOrLeadsStatus = (e) => {
    setFilterBy(e?.value);

    // let data = {
    //   case_id: selectedCaseId?.id,
    //   firm_user_id: selectedUser ? [selectedUser] : '',
    //   time_entry_status: selectedStatus || '',
    //   ...pickExtractedDate(selectedDate),
    //   time_entry_filter: 0,
    //   search_key: searchKey,
    //   billing_status: e?.value ? e?.value : '',
    //   practice_area: selectedPractice,
    // };
    // loadTimeEntries(data, pageLimit, '', 'forward');
  };

  const getCaseDetails = (tenantId, tenant_sk) => {
    totalCountEvent('case').then((response) => {
      getCase(tenantId, tenant_sk, '', '', '', '', '', '', response?.data, '', 'forward', '').then((response) => {
        const caseList = response?.data?.case?.map((data) => ({
          label: data?.case_description?.case_name,
          value: data?.case_id,
        }));
        setCaseListOptions(caseList);
      });
    });
  };

  const getLeadDetails = (tenantId, tenant_sk) => {
    totalCountEvent('lead').then((response) => {
      getLeads(false, false, false, response?.data).then((response) => {
        const leadList = response?.data?.leads
          ?.map((data) => {
            const id = data?.sk?.split('#')[1];
            const label = `${data?.main_client?.first_name} ${data?.main_client?.last_name}`;
            if (!data?.is_draft) {
              return { label: label, value: id };
            }
          })
          .filter((data) => data?.label !== undefined);
        setLeadListOptions(leadList);
      });
    });
  };

  useEffect(() => {
    const tenantId = cookie.get('tenantId') || null;
    const tenant_sk = cookie.get('tenantSk') || null;
    module === 'case' ? getCaseDetails(tenantId, tenant_sk) : getLeadDetails();
  }, [module]);

  return (
    <div>
      {!isFromDashBoard && <GlobalLoader />}
      <div className="card-bg p-3 mt-3">
        {!isFromTeamManagement && !isFromReports && <h5 className="F-size14 mb-3 black-600 text-bold">Activity Log</h5>}
        {isFromReports && <h5 className="F-size14 mb-3 black-600 text-bold">Activity Log</h5>}
        <div className="d-flex justify-content-end align-items-center flex-wrap mb-3 filter-wrap">
          {isFromTeamManagement ? (
            <div className="d-flex align-items-center">
              <div className="me-3 black-800 text-bold">
                {currentDateToDisplay.end.toDateString().split(' ')[1]} {currentDateToDisplay.start.toDateString().split(' ')[2]}-
                {currentDateToDisplay.end.toDateString().split(' ')[2]}
              </div>

              <div
                className="rounded me-1 pointer slider-ctrl"
                onClick={(e) => {
                  fetchActivityLogData(true, false);
                }}
              >
                <i className="pi pi-angle-left p-1"></i>
              </div>
              <div
                className={
                  currentDateToDisplay.end.toDateString() === todayDate.toDateString()
                    ? 'rounded ms-1 pointer slider-ctrl disabled-element '
                    : 'rounded ms-1 pointer slider-ctrl'
                }
                onClick={(e) => fetchActivityLogData(false, true)}
              >
                <i className="pi pi-angle-right p-1"></i>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
              <label className="text-bold black-600  filter-label">Filters:</label>
              <div className="d-flex flex-column mx-lg-2 filter-wraper">
                <TimezoneDatePicker
                  timezone={userContext?.userDetails?.timezone}
                  className="input-shadow filter-calender"
                  id="icon"
                  value={dateFilter}
                  onChange={(e) => {
                    onChangeCalendar(e);
                  }}
                  selectionMode="range"
                  showIcon
                  placeholder="Date"
                  isEndOfDay={true}
                />
              </div>
              <div className="d-flex flex-column mx-lg-2 filter-wraper">
                <Dropdown
                  placeholder={props?.caseDetails?.case_id ? 'By Case User' : 'By User'}
                  optionLabel={(v) => generateFullName(v)}
                  onChange={(e) => setSelectedUserList(e.value)}
                  options={usersList}
                  value={selectedUserList}
                  className="mx-1 p-datatable-responsive"
                  filter
                  showClear
                  style={{ minWidth: 200 }}
                  itemTemplate={assignToItemTemplate}
                />
              </div>
              <div className="d-flex flex-column my-2 filter-wraper">
                <Dropdown options={activityLogModuleFilter} onChange={onChangeCaseOrLeadsStatus} value={filterBy} placeholder="Module" />
              </div>
              {isFromReports && (
                <div className="d-flex flex-column my-2 mx-1 filter-wraper">
                  <Dropdown options={caseLeadsFilter} onChange={(e) => setModule(e?.value)} value={module} placeholder="Sub Module" />
                </div>
              )}

              {isFromReports && (module === 'case' || module === 'lead') && (
                <div className="d-flex flex-column my-2 filter-wraper">
                  <Dropdown
                    value={moduleSection}
                    options={module === 'case' ? caseListOptions : module === 'lead' ? leadListOptions : ''}
                    onChange={(e) => {
                      setModuleSection(e?.value);
                    }}
                    placeholder={module === 'case' ? 'Select Case' : module === 'lead' ? 'Select Lead' : ''}
                    filter
                  />
                </div>
              )}

              {/* {!isFromCaseSpecificModule && !props?.isLeadSpecific && (
                  <div className="d-flex flex-column mx-lg-2 mx-0 filter-wraper">
                    <MultiSelect
                      value={selectedPractice}
                      options={practiceAreaOptions}
                      onChange={onChangePractice}
                      placeholder="Practice Area"
                      maxSelectedLabels={1}
                      display="chip"
                      filter
                      className="input-shadow w-100 F-size14 input-width"
                      panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
                    />
                  </div>
                )} 
                  */}

              <div className="d-flex flex-column mx-2">
                <div className="pointer ms-0">
                  <i className="fas fa-sync-alt" onClick={refreshCase}></i>
                  <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
                </div>
              </div>
            </div>
          )}
        </div>
        <ActivityLogTable
          activityLog={activityLog}
          isCaseSpecific={isCaseSpecific}
          isLead={props?.isLead}
          isFromDashBoard={props?.isFromDashBoard}
        />
      </div>
    </div>
  );
};

export default ActivityLog;
